import React from 'react'
import frauncesLatinUrl from '@fontsource-variable/fraunces/files/fraunces-latin-full-normal.woff2?url'
import {
	data,
	type LoaderFunctionArgs,
	type HeadersFunction,
	type LinksFunction,
	type MetaFunction,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useLocation,
	useMatches,
	useRouteLoaderData,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import { GeneralErrorBoundary } from './components/templates/ErrorBoundary.tsx'
import { EpicProgress } from './components/atoms/ProgressBar.tsx'
import { SiteDock } from './components/organisms/site/SiteDock.tsx'
import {
	SiteHeader,
	staffLinksFor,
} from './components/organisms/site/SiteHeader.tsx'
import { useToast } from './components/templates/Toaster.tsx'
import { href as iconsHref } from './components/atoms/icon.tsx'
import { EpicToaster } from './components/atoms/sonner.tsx'
import { useTheme } from './routes/resources+/theme-switch.tsx'
import cartFlyStyleUrl from './styles/cart-fly.css?url'
import stampStyleUrl from './styles/stamp.css?url'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { loadCartQuote, payAllQuote } from '#app/domains/payments'
import { getEnv } from './utils/env.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { pageMeta } from './utils/page-meta.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import { useOptionalUser, userHasPermission } from './utils/user.ts'
export const links: LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		// the display face (headings everywhere)
		{
			rel: 'preload',
			href: frauncesLatinUrl,
			as: 'font',
			type: 'font/woff2',
			crossOrigin: 'anonymous',
		} as const,
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
		{ rel: 'stylesheet', href: stampStyleUrl },
		{ rel: 'stylesheet', href: cartFlyStyleUrl },
	].filter(Boolean)
}

// every route without a meta of its own shows these
export const meta: MetaFunction<typeof loader> = (args) =>
	pageMeta(args, { title: args.loaderData ? null : 'Feil' })

export async function loader({ request }: LoaderFunctionArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUniqueOrThrow({
						select: {
							id: true,
							name: true,
							username: true,
							image: { select: { id: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}

	// Dock checkout badge: the buyer's unpaid total. Cheap local-SQLite
	// queries; never allowed to break any page.
	let checkoutBadge: { total: number; count: number } | null = null
	if (user) {
		try {
			checkoutBadge = await time(() => loadCheckoutBadge(user.id), {
				timings,
				type: 'checkoutBadge',
				desc: 'checkout badge in root',
			})
		} catch (error) {
			console.error('checkout badge failed', error)
		}
	}

	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = honeypot.getInputProps()

	return data(
		{
			user,
			checkoutBadge,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

/**
 * What the buyer still owes in the current festival, as the checkout shows
 * it: the cart's per-ticket prices on a PER_CONCERT festival (ADR 0002),
 * otherwise the legacy per-person pay-all quote (ADR 0001).
 */
async function loadCheckoutBadge(buyerId: string) {
	const festival = await prisma.festival.findFirst({
		where: { isCurrent: true },
		select: { pricingModel: true },
	})
	if (festival?.pricingModel === 'PER_CONCERT') {
		const cart = await loadCartQuote(prisma, buyerId)
		return { total: cart?.total ?? 0, count: cart?.tickets.length ?? 0 }
	}
	const quote = await payAllQuote(prisma, buyerId)
	return {
		total: quote.total,
		count: quote.persons.reduce((sum, p) => sum + p.ticketCount, 0),
	}
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
	const headers = {
		'Server-Timing': loaderHeaders.get('Server-Timing') ?? '',
	}
	return headers
}

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
	allowIndexing = true,
	night = false,
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string | undefined>
	allowIndexing?: boolean
	/** the page opens on the night sky (front page): tint the browser bar */
	night?: boolean
}) {
	return (
		<html
			lang="nn"
			className={`${theme} h-full overflow-x-hidden`}
			// iOS Safari tints its bars from the page background, not theme-color
			data-night={night ? '' : undefined}
		>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				{/* in the document, not in a meta export: a route's own meta
				    replaces the root's, and phones without it lay out at 980px */}
				<meta
					name="viewport"
					content="width=device-width,initial-scale=1,viewport-fit=cover"
				/>
				<meta name="mobile-web-app-capable" content="yes" />
				<meta name="apple-mobile-web-app-capable" content="yes" />
				<meta name="apple-mobile-web-app-status-bar-style" content="default" />
				<meta name="theme-color" content={night ? '#121827' : '#f6efdf'} />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
				<meta property="fb:app_id" content="890080053017881" />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

/** The dock's staff entrance: admins the ticket list, door staff the door */
function dockStaff(user: ReturnType<typeof useOptionalUser>) {
	if (user?.roles.some((role) => role.name === 'admin')) return 'admin'
	return userHasPermission(user, 'read:ticket:any') ? 'door' : null
}

function App() {
	const data = useLoaderData<typeof loader>()
	const nonce = useNonce()
	const user = useOptionalUser()
	const theme = useTheme()
	const location = useLocation()
	const allowIndexing = data.ENV.ALLOW_INDEXING !== 'false'
	useToast(data.toast)

	const staffLinks = staffLinksFor(user)
	const staff = dockStaff(user)
	// pages drawn for a camera (the share card) leave the site chrome out
	const chrome = !useMatches().some(
		(match) =>
			(match.handle as { chrome?: boolean } | undefined)?.chrome === false,
	)

	return (
		<Document
			nonce={nonce}
			theme={theme}
			allowIndexing={allowIndexing}
			env={data.ENV}
			night={location.pathname === '/'}
		>
			{chrome ? (
				<div className="site-main">
					<SiteHeader
						user={user}
						staffLinks={staffLinks}
						overlay={location.pathname === '/'}
					/>
					<Outlet />
				</div>
			) : (
				<Outlet />
			)}
			<EpicToaster closeButton position="top-center" theme={theme} />
			<EpicProgress />
			{chrome ? (
				<SiteDock
					user={user}
					staff={staff}
					unpaidTotal={data.checkoutBadge?.total ?? null}
				/>
			) : null}
		</Document>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default AppWithProviders

export function ErrorBoundary() {
	// the nonce doesn't rely on the loader so we can access that
	const nonce = useNonce()
	// usually the root loader did run (the error came from a page below it):
	// then keep the header and the dock, so there is always a way on
	const data = useRouteLoaderData<typeof loader>('root')
	const user = useOptionalUser()

	return (
		<Document
			nonce={nonce}
			theme={data?.requestInfo.userPrefs.theme ?? 'light'}
			env={data?.ENV}
		>
			<div className="site-main">
				{data ? (
					<SiteHeader
						user={user}
						staffLinks={staffLinksFor(user)}
						overlay={false}
					/>
				) : null}
				<GeneralErrorBoundary />
			</div>
			{data ? (
				<SiteDock user={user} staff={dockStaff(user)} unpaidTotal={null} />
			) : null}
		</Document>
	)
}
